import { useState } from "react";
import { useNotify, useRefresh, useRecordContext } from "ra-core";
import { FileText, Loader2, FileSpreadsheet, FileBox, Truck, Receipt, Ship, Package, FileCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Document generation configuration
const DOCUMENT_GROUPS = [
  {
    name: "Orders",
    documents: [
      { id: "sales_order", name: "Sales Order", icon: FileText },
      { id: "purchase_order", name: "Purchase Order", icon: FileSpreadsheet },
    ],
  },
  {
    name: "Shipping",
    documents: [
      { id: "bill_of_lading", name: "Bill of Lading", icon: Truck },
      { id: "booking_request", name: "Booking Request", icon: Ship },
      { id: "booking_confirmation", name: "Booking Confirmation", icon: FileCheck },
      { id: "pickup_confirmation", name: "Pickup Confirmation", icon: Package },
      { id: "freight_request", name: "Freight Request", icon: Truck },
    ],
  },
  {
    name: "Customs & Invoicing",
    documents: [
      { id: "customs_invoice", name: "Customs Invoice", icon: Receipt },
      { id: "booking_sheet", name: "Booking Sheet", icon: FileBox },
    ],
  },
  {
    name: "Internal Forms",
    documents: [
      { id: "credit_request", name: "Credit Request", icon: FileText },
      { id: "wire_transfer_request", name: "Wire Transfer Request", icon: FileText },
    ],
  },
];

export const DocumentsPanel = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [generating, setGenerating] = useState<string | null>(null);

  if (!record) return null;

  const handleGenerate = async (documentType: string, documentName: string) => {
    setGenerating(documentType);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "generate_document",
        {
          body: {
            load_id: record.id,
            document_type: documentType,
          },
        }
      );

      if (error) {
        throw error;
      }

      if (data?.success) {
        notify(`${documentName} generated successfully`, { type: "success" });
        refresh();
      } else {
        throw new Error(data?.error || "Unknown error");
      }
    } catch (err: any) {
      console.error("Document generation error:", err);
      notify(
        err?.message || `Failed to generate ${documentName}`,
        { type: "error" }
      );
    } finally {
      setGenerating(null);
    }
  };

  return (
    <Card>
      <CardContent className="pt-4">
        <h2 className="text-base font-semibold mb-4">Generate Documents</h2>
        <Separator className="mb-4" />
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {DOCUMENT_GROUPS.map((group) => (
            <div key={group.name} className="space-y-3">
              <h3 className="text-sm font-medium text-muted-foreground">
                {group.name}
              </h3>
              <div className="space-y-2">
                {group.documents.map((doc) => {
                  const Icon = doc.icon;
                  const isGenerating = generating === doc.id;
                  
                  return (
                    <Button
                      key={doc.id}
                      variant="outline"
                      size="sm"
                      className="w-full justify-start gap-2 text-left"
                      onClick={() => handleGenerate(doc.id, doc.name)}
                      disabled={!!generating}
                    >
                      {isGenerating ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Icon className="h-4 w-4" />
                      )}
                      <span className="truncate">{doc.name}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <Separator className="my-4" />
        
        <p className="text-xs text-muted-foreground">
          Generated documents will be attached to this load as notes and stored in the attachments bucket.
          You can view and download them from the Activity section below.
        </p>
      </CardContent>
    </Card>
  );
};
